// FUNCTIONALITY-115C-R2 / CUSTOMER-172B / R7.2.2F4 — floating quick scroll.
// F4: remove persistent scroll-down FAB (it covered commerce CTAs). Keep compact scroll-up after depth only.
import { useEffect, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  resolveStoreScrollRoot,
  scrollStoreToAbsoluteTop,
} from "./quick-scroll-helpers-v2.js";

export {
  resolveStoreScrollRoot,
  scrollStoreToAbsoluteTop,
} from "./quick-scroll-helpers-v2.js";

const KEYBOARD_ATTR = "data-as-keyboard-open";
/** Show ↑ only after meaningful scroll (not near top). */
const SHOW_UP_AFTER_PX = 280;

export function QuickScrollControls({
  lang,
  hide = false,
}: {
  lang: StoreV2Lang;
  /** Hide on search route so FABs do not cover sticky search in narrow Telegram Desktop. */
  hide?: boolean;
}) {
  const [showUp, setShowUp] = useState(false);

  useEffect(() => {
    const root = resolveStoreScrollRoot();
    if (!root) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const next = root.scrollTop > SHOW_UP_AFTER_PX;
        setShowUp((prev) => (prev === next ? prev : next));
      });
    };
    onScroll();
    root.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      root.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let settle: ReturnType<typeof setTimeout> | null = null;
    const apply = () => {
      const shrink = window.innerHeight - vv.height;
      const open = shrink > 120;
      const root = document.documentElement;
      if (open) root.setAttribute(KEYBOARD_ATTR, "1");
      else root.removeAttribute(KEYBOARD_ATTR);
    };
    const onResize = () => {
      if (settle) clearTimeout(settle);
      settle = setTimeout(apply, 100);
    };
    apply();
    vv.addEventListener("resize", onResize);
    return () => {
      if (settle) clearTimeout(settle);
      vv.removeEventListener("resize", onResize);
      document.documentElement.removeAttribute(KEYBOARD_ATTR);
    };
  }, []);

  if (hide || !showUp) return null;

  return (
    <div
      className="sv2-quick-scroll sv2-quick-scroll--up-only"
      role="group"
      aria-label={lang === "en" ? "Quick scroll" : "Швидкий скрол"}
      data-scroll-down="removed"
    >
      <button
        type="button"
        className="sv2-quick-scroll-btn"
        onClick={() => scrollStoreToAbsoluteTop()}
        aria-label={lang === "en" ? "Back to top" : "До початку сторінки"}
      >
        {lang === "en" ? "↑ Up" : "↑ Вгору"}
      </button>
    </div>
  );
}
